import { useState, useCallback } from 'react';
import olreadiaService from '../services/olreadiaService';

const toMessage = (err: any, fallback: string) =>
  err?.response?.data?.message || err?.message || fallback;

export const useBusinessUsers = (businessEntityId: string | null) => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState({ totalElements: 0, totalPages: 1 });

  const list = useCallback(async (params = {}) => {
    if (!businessEntityId) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await olreadiaService.businessEntities.users.list(businessEntityId, params);
      const content = Array.isArray(res?.content) ? res.content : [];
      setUsers(content);
      setPage({
        totalElements: res?.totalElements ?? content.length,
        totalPages: res?.totalPages ?? 1,
      });
      return res;
    } catch (err) {
      const message = toMessage(err, 'Failed to load business users');
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [businessEntityId]);

  const add = useCallback(async (payload: any, actorId?: string | null) => {
    if (!businessEntityId) throw new Error('Business entity ID required');
    try {
      return await olreadiaService.businessEntities.users.add(businessEntityId, payload, actorId);
    } catch (err) {
      throw err;
    }
  }, [businessEntityId]);

  const update = useCallback(async (userId: string, payload: any, actorId?: string | null) => {
    if (!businessEntityId) throw new Error('Business entity ID required');
    try {
      return await olreadiaService.businessEntities.users.update(businessEntityId, userId, payload, actorId);
    } catch (err) {
      throw err;
    }
  }, [businessEntityId]);

  const remove = useCallback(async (userId: string, actorId?: string | null) => {
    if (!businessEntityId) throw new Error('Business entity ID required');
    try {
      await olreadiaService.businessEntities.users.remove(businessEntityId, userId, actorId);
    } catch (err) {
      throw err;
    }
  }, [businessEntityId]);

  return { users, loading, error, page, list, add, update, remove };
};

export default useBusinessUsers;
